import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { usePreventWindowUnload, useQuery } from '@panel/commons/hooks';
import WrapperContainer from '../../Wrappers/WrapperContainer';
import NoAvailablePlans from './NoAvailablePlans';
import PlanSelector from './PlanSelector';
import SelectSection from './SelectSection';
import adaptAvailablePlans from './adapters/adaptAvailablePlans';
import adaptUsersToOptions from './adapters/adaptUsersToOptions';
import adaptBranchesToOptions from './adapters/adaptBranchesToOptions';
import adaptComercialInfo from './adapters/adaptComercialInfo';
import { showSelectSectionPublish, getEnabledShareCommissionSites } from './utils';
import { getStepPublishSelectors, StepPublishOperations } from '../../../ducks/publish';
import { SavePostingOperations } from '../../../ducks/savePosting';
import useFormIsDirty from '../../../hooks/useFormIsDirty';
import { setCookieWithTTL } from '@panel/commons/helpers/cookieHelper';
import ShareCommissionSection from './ShareCommissionSection/index.jsx';

const Publish = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [postingId, isEdition, republish, publisherIdOrigen] = useQuery(useLocation(), [
    'postingId',
    'isEdition',
    'republish',
    'publisherIdOrigen'
  ]);
  const { setFormIsDirty } = useFormIsDirty();
  const { formValues, formValuesCompleted, formValuesLoading, formValuesError, isZonaDemandLocation } = useSelector(
    getStepPublishSelectors
  );
  // usamos un firstLoad para evitar que se detecten cambios en el primer renderizado
  const [firstLoad, setFirstLoad] = useState(true);
  // usamos un hasChanges para detectar si se han realizado cambios en el formulario ya que formState.isDirty se actualiza en el primer render para marcar el plan
  const [hasChanges, setHasChanges] = useState(false);
  const hasAutoSelectedBranchRef = useRef(false);
  const { register, handleSubmit, watch, setValue, clearErrors } = useForm({
    defaultValues: {
      ...formValues,
      responsible: formValues.currentResponsible,
      branch: formValues.currentBranch
    },
    mode: 'onChange'
  });

  usePreventWindowUnload(hasChanges);

  useEffect(() => {
    register('plan_name');
  }, [register]);

  // Resetear el formulario cuando cambian los valores de Redux (solo en carga inicial)
  useEffect(() => {
    if (formValuesCompleted && !formValuesLoading && firstLoad) {
      setValue('responsible', formValues.currentResponsible);
      setValue('branch', formValues.currentBranch);
      setValue('share_commission', formValues.commissionShare);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValuesCompleted, formValuesLoading, firstLoad]);

  // Actualizar la propiedad isResponsible basándose en el responsable actual de Redux
  const usersWithCurrentResponsible = formValues.usersAllowed.map(user => ({
    ...user,
    isResponsible: user.userId === formValues.currentResponsible
  }));
  const adaptedUsersOptions = adaptUsersToOptions(usersWithCurrentResponsible);
  const adaptedBranches = adaptBranchesToOptions(formValues.branches, formValues.currentBranch);
  const plans = adaptAvailablePlans(
    formValues.availableCredits.availablePlans,
    t,
    formValues.availableCredits?.currentPlan || null,
    isEdition,
    formValues.branches,
    formValues.currentBranch,
    formValues.availableCredits?.postingStatus
  );
  const { showSelectSection, hideSelectBranches, isUsersEmpty, isPlansEmpty, defaultUserId } = showSelectSectionPublish(
    adaptedBranches,
    adaptedUsersOptions,
    plans,
    formValues.availableCredits.postingStatus
  );
  const comercialInfoAdapted = adaptComercialInfo(formValues?.comercialInfo, t);
  const currentBranch = watch('branch');
  const currentResponsible = watch('responsible');
  const currentPlan = watch('plan');
  const currentPlanName = watch('plan_name');
  const currentCommissionShare = watch('share_commission');

  /**
   * Maneja cambios en los campos del formulario y elimina los errores del form.
   * @param {string} fieldName - Nombre del campo que cambió
   * @param {*} value - Nuevo valor del campo
   */
  const handleFieldChange = (fieldName, value) => {
    const current = watch(fieldName);

    if (current !== value) {
      // Si se cambia algun campo y no es la carga inicial de datos, seteamos el estado de cambios
      if (!firstLoad) {
        setHasChanges(true);
      }
      setValue(fieldName, value, { shouldDirty: true });
      clearErrors(fieldName);

      if (fieldName === 'responsible') {
        dispatch(StepPublishOperations.updateSelectedResponsible(value));
      }
      if (fieldName === 'branch') {
        dispatch(StepPublishOperations.updateSelectedBranch(value));
      }
    }
    setFirstLoad(false);
  };

  useEffect(() => {
    setFormIsDirty(hasChanges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges]);

  useEffect(() => {
    if (postingId && !formValuesError) {
      dispatch(StepPublishOperations.getInitialsValues(postingId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId]);

  useEffect(() => {
    if (defaultUserId) {
      handleFieldChange('responsible', defaultUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultUserId]);

  // Sincronizar el formulario cuando se actualiza el responsable en Redux (solo para carga inicial)
  useEffect(() => {
    // Solo sincronizar si el valor de Redux es diferente y no es nulo
    if (formValues.currentResponsible && formValues.currentResponsible !== currentResponsible && formValuesCompleted) {
      setValue('responsible', formValues.currentResponsible);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.currentResponsible, formValuesCompleted]);

  useEffect(() => {
    if (formValues.currentBranch && formValues.currentBranch !== currentBranch && formValuesCompleted) {
      setValue('branch', formValues.currentBranch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.currentBranch, formValuesCompleted]);

  // Sucursal deshabilitada: autoseleccionar la primera sucursal habilitada y cargar sus créditos (mismo flujo que el dropdown)
  useEffect(() => {
    if (
      !publisherIdOrigen ||
      !formValuesCompleted ||
      formValuesLoading ||
      hideSelectBranches ||
      hasAutoSelectedBranchRef.current
    ) {
      return;
    }

    const firstBranchId = adaptedBranches[0]?.id;
    if (!firstBranchId || !postingId) return;

    if (String(firstBranchId) === String(publisherIdOrigen)) {
      hasAutoSelectedBranchRef.current = true;
      return;
    }

    hasAutoSelectedBranchRef.current = true;
    dispatch(StepPublishOperations.getCreditsOfBranch(postingId, firstBranchId));
    handleFieldChange('branch', firstBranchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    publisherIdOrigen,
    formValuesCompleted,
    formValuesLoading,
    hideSelectBranches,
    adaptedBranches.length,
    postingId
  ]);

  const showTip =
    !formValuesLoading && formValuesCompleted && isPlansEmpty && isZonaDemandLocation
      ? [t('professionalPubli.tip.availablePlans')]
      : [];

  const setResponsible = async () => {
    if (currentResponsible) {
      await dispatch(StepPublishOperations.updatePostingResponsible(postingId, currentResponsible));
      dispatch(StepPublishOperations.updateSelectedResponsible(currentResponsible));
    }
    if (currentBranch) {
      const ownerBranch = formValues?.branches?.find(branch => branch.isOwner === true);
      const originBranchId = ownerBranch?.branchId ?? publisherIdOrigen;

      if (originBranchId && String(originBranchId) !== String(currentBranch)) {
        await dispatch(StepPublishOperations.updatePostingBranch(postingId, originBranchId, currentBranch));
      }
      dispatch(StepPublishOperations.updateSelectedBranch(currentBranch));
    }
  };

  const onSubmit = async () => {
    if (republish) {
      setCookieWithTTL('tryRepublishFromPublishStep', 'true', 15);
    }
    await setResponsible();
    if (currentPlan && !isPlansEmpty) {
      const payload = {
        body: {
          postingId: postingId,
          publication_plan: currentPlan,
          commission_share: currentCommissionShare ?? 0
        },
        step: 'STEP_PLAN_SELECTION'
      };
      dispatch(SavePostingOperations.savePostingDB(payload));
      // Actualizar directamente el estado Redux con el plan seleccionado
      dispatch(StepPublishOperations.updateSelectedPlan(currentPlan));
      dispatch(StepPublishOperations.updateCommissionShare(currentCommissionShare));
    }
    if (isPlansEmpty) {
      setCookieWithTTL('postingSavedMessage', 'panel.fromPublisherProf.draft.snackbar.SUCCESS');
      history.push('/avisos');
    }
  };

  const getIsValidForm = async () => {
    return true;
  };

  return (
    <WrapperContainer
      id='publish-step'
      onSubmit={handleSubmit(onSubmit)}
      title={hideSelectBranches && isUsersEmpty && isPlansEmpty ? '' : t('professionalPubli.publish.title')}
      formValuesLoading={formValuesLoading}
      loadError={formValuesError}
      resetInfo={() => {
        dispatch(StepPublishOperations.resetInfo());
      }}
      tips={showTip}
      btnSubmitText={isPlansEmpty ? t('professionalPubli.cta.saveAsDraft') : t('professionalPubli.cta.publish')}
      isPlanEmpty={isPlansEmpty && formValuesCompleted && !formValuesLoading}
      planName={currentPlanName}
      getIsValidForm={getIsValidForm}
    >
      <form id='publish-step'>
        <fieldset disabled={formValuesLoading}>
          {showSelectSection && formValuesCompleted && (
            <SelectSection
              adaptedUsersOptions={adaptedUsersOptions}
              adaptedBranches={adaptedBranches}
              hideSelectBranches={hideSelectBranches}
              isUsersEmpty={isUsersEmpty}
              postingId={postingId}
              handleFieldChange={handleFieldChange}
              register={register}
              watch={watch}
              data-publi='publicador-publishStep-selectSection'
            />
          )}
          {!isPlansEmpty && formValuesCompleted && (
            <PlanSelector
              plans={plans}
              handleFieldChange={handleFieldChange}
              register={register}
              watch={watch}
              hasPlanSelected={formValues.availableCredits?.currentPlan?.publication_plan_id || null}
              dataPubli='publicador-publishStep-planSelector'
            />
          )}
          {getEnabledShareCommissionSites() && !isPlansEmpty && formValuesCompleted && (
            <ShareCommissionSection
              register={register}
              handleFieldChange={handleFieldChange}
              percentage_shared={formValues.commissionShare}
            />
          )}
          {isPlansEmpty && formValuesCompleted && (
            <NoAvailablePlans contactInfo={comercialInfoAdapted} isZonaDemandLocation={isZonaDemandLocation} />
          )}
        </fieldset>
      </form>
    </WrapperContainer>
  );
};
export default Publish;
